import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById, selectIsLoading } from '../selectors.ts';
import { useAttach } from '../useAttach';

import {
  advancedFlowsStore,
  type AdvancedFlowsStoreState,
  type AdvancedFlowProps,
} from './AdvancedFlowsStore.ts';
import { AdvancedFlowUtils } from './AdvancedFlowUtils.js';

export function useAdvancedFlowsData() {
  useAttach(advancedFlowsStore, 'useAdvancedFlowsData');
  return useStoreWithEqualityFn(advancedFlowsStore.store, selectData, shallow);
}
useAdvancedFlowsData.store = advancedFlowsStore;

export function useAdvancedFlowsById() {
  useAttach(advancedFlowsStore, 'useAdvancedFlowsById');
  return useStoreWithEqualityFn(advancedFlowsStore.store, selectById, shallow);
}
useAdvancedFlowsById.store = advancedFlowsStore;

export function useAdvancedFlowsByFolderId() {
  useAttach(advancedFlowsStore, 'useAdvancedFlowsByFolderId');

  const select = useCallback((state: AdvancedFlowsStoreState) => {
    if (state.byId == null) {
      return { byFolderId: null };
    }

    const byFolderId: Record<string, Record<string, AdvancedFlowProps>> = {};

    for (const advancedFlow of Object.values(state.byId)) {
      byFolderId[String(advancedFlow.folder)] = byFolderId[String(advancedFlow.folder)] ?? {};
      byFolderId[String(advancedFlow.folder)][advancedFlow.id] = advancedFlow;
    }

    return {
      byFolderId: byFolderId,
    };
  }, []);

  return useStoreWithEqualityFn(advancedFlowsStore.store, select, shallow);
}
useAdvancedFlowsByFolderId.store = advancedFlowsStore;

export function useAdvancedFlowsByCardOwnerUri() {
  useAttach(advancedFlowsStore, 'useAdvancedFlowsByCardOwnerUri');

  const select = useCallback((state: AdvancedFlowsStoreState) => {
    if (state.byId == null) {
      return { byCardOwnerUri: null };
    }

    const byCardOwnerUri: Record<string, Record<string, AdvancedFlowProps>> = {};
    for (const advancedFlow of Object.values(state.byId)) {
      const cardOwnerUris = AdvancedFlowUtils.getOwnerUrisSetFromCards(advancedFlow);

      for (const cardOwnerUri of cardOwnerUris.values()) {
        byCardOwnerUri[cardOwnerUri] = byCardOwnerUri[cardOwnerUri] ?? {};
        byCardOwnerUri[cardOwnerUri][advancedFlow.id] = advancedFlow;
      }
    }

    return {
      byCardOwnerUri: byCardOwnerUri,
    };
  }, []);

  return useStoreWithEqualityFn(advancedFlowsStore.store, select, shallow);
}
useAdvancedFlowsByCardOwnerUri.store = advancedFlowsStore;

export function useAdvancedFlow({ advancedFlowId }: { advancedFlowId: string }) {
  useAttach(advancedFlowsStore, 'useAdvancedFlow');

  const select = useCallback(
    (state: AdvancedFlowsStoreState) => {
      return {
        advancedFlow: state.byId?.[advancedFlowId] ?? null,
        advancedFlowInstance: state.data?.[advancedFlowId] ?? null,
        isLoading: state.isLoading,
        error: state.error,
      };
    },
    [advancedFlowId],
  );

  return useStoreWithEqualityFn(advancedFlowsStore.store, select, shallow);
}
useAdvancedFlow.store = advancedFlowsStore;

export function useAdvancedFlowsAttach() {
  useAttach(advancedFlowsStore, 'useAdvancedFlowsAttach');
}
useAdvancedFlowsAttach.store = advancedFlowsStore;

export function useAdvancedFlowsLoading() {
  useAttach(advancedFlowsStore, 'useAdvancedFlowsLoading');
  return useStoreWithEqualityFn(advancedFlowsStore.store, selectIsLoading, shallow);
}
useAdvancedFlowsLoading.store = advancedFlowsStore;
